import { useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import PageHeader from "@/components/marketing/PageHeader";
import { IMG } from "@/data/content";
import { API, getVisitorId } from "@/lib/tracking";

const inputClass = "border border-black/20 p-3 rounded bg-white focus:outline-none focus:ring-2 focus:ring-rc-red";

function Field({ label, children, className = "" }) {
  return <label className={`grid gap-1 text-sm font-semibold text-rc-charcoal ${className}`}><span>{label}</span>{children}</label>;
}

export default function RequestPage() {
  const order = useLocation().pathname.includes("order-dispatch");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    setSubmitting(true);
    setStatus("Sending…");
    try {
      if (order) {
        await axios.post(`${API}/requests/asphalt-orders`, {
          ...values,
          tonnage: Number(values.tonnage),
          truck_count: Number(values.truck_count),
          visitor_id: getVisitorId(),
        });
      } else {
        const data = new FormData(form);
        data.append("visitor_id", getVisitorId());
        await axios.post(`${API}/requests/bid-rfp`, data);
      }
      form.reset();
      setStatus("Thank you — our team has received your request.");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setStatus(typeof detail === "string" ? detail : "We could not submit your request. Please call the office directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const title = order ? "Direct Asphalt Dispatch & Contractor Order Desk" : "Request an Estimate / Submit an RFP";
  return (
    <div data-testid={order ? "asphalt-order-page" : "bid-request-page"}>
      <PageHeader
        overline={order ? "Asphalt Plants" : "Contact Us"}
        crumb={title}
        title={title}
        subtitle={order ? "Rapid truck turn times, high-volume batching, and responsive dispatch coordination." : "Send your project drawings and bid requests directly to our estimating department."}
        image={IMG.asphaltBanner}
      />
      <section className="bg-rc-bg py-20">
        {order && <div className="max-w-3xl mx-auto mb-6 grid sm:grid-cols-2 gap-3 text-sm text-rc-brown"><div>Winter Garden / Orlando: (407) 656-9255</div><div>Fort Pierce: (772) 464-6460</div><div>West Palm Beach: (561) 793-9400</div><div>Miami / South Florida: (561) 793-9400</div></div>}
        <form onSubmit={submit} className="max-w-3xl mx-auto bg-white border border-black/10 rounded-2xl p-7 grid md:grid-cols-2 gap-4">
          <Field label="Name"><input required minLength={2} name="name" autoComplete="name" className={inputClass} /></Field>
          <Field label="Company"><input required minLength={2} name="company" autoComplete="organization" className={inputClass} /></Field>
          <Field label="Email"><input required type="email" name="email" autoComplete="email" className={inputClass} /></Field>
          <Field label="Phone"><input required minLength={7} type="tel" name="phone" autoComplete="tel" className={inputClass} /></Field>
          {order ? (
            <>
              <Field label="Pickup plant">
                <select required name="plant" defaultValue="" className={inputClass}>
                  <option value="" disabled>Select a plant</option>
                  <option>Winter Garden / Orlando</option><option>Fort Pierce / Treasure Coast</option><option>West Palm Beach</option><option>Miami / South Florida</option>
                </select>
              </Field>
              <Field label="Mix selection">
                <select required name="mix" defaultValue="" className={inputClass}>
                  <option value="" disabled>Select a mix</option>
                  <option>SP-9.5</option><option>SP-12.5</option><option>SP-19.0</option><option>FC-5</option><option>FC-9.5</option><option>FC-12.5</option><option>RAP / custom mix</option>
                </select>
              </Field>
              <Field label="Pickup date"><input required name="pickup_date" type="date" min={new Date().toISOString().slice(0, 10)} className={inputClass} /></Field>
              <Field label="Requested tonnage"><input required name="tonnage" type="number" min="0.1" max="100000" step="0.1" className={inputClass} /></Field>
              <Field label="Fleet truck count"><input required name="truck_count" type="number" min="1" max="10000" step="1" className={inputClass} /></Field>
            </>
          ) : (
            <>
              <Field label="Project type">
                <select required name="project_type" className={inputClass}>
                  <option>FDOT / Heavy Highway</option><option>Commercial Site Work</option><option>Asphalt Paving / Milling</option><option>Aviation / Port</option><option>FOB Asphalt Supply</option>
                </select>
              </Field>
              <Field label="Project location / city"><input required name="project_location" className={inputClass} /></Field>
              <Field label="Plans, specifications, or RFP document" className="md:col-span-2">
                <input name="attachment" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.dwg" className={`${inputClass} file:mr-3 file:border-0 file:bg-rc-charcoal file:text-white file:px-3 file:py-2`} />
                <span className="font-normal text-xs text-rc-brown">PDF, Office document, image, or DWG up to 25 MB.</span>
              </Field>
            </>
          )}
          <Field label={order ? "Order notes" : "Project details"} className="md:col-span-2">
            <textarea name="details" maxLength={5000} className={`${inputClass} min-h-32`} />
          </Field>
          <label className="md:col-span-2 flex items-start gap-3 text-sm text-rc-brown cursor-pointer">
            <input type="checkbox" name="marketing_consent" value="true" className="mt-1" />
            <span>Yes, I would like to receive Ranger Construction news, project updates, and relevant communications. I understand I can unsubscribe at any time.</span>
          </label>
          <button disabled={submitting} className="bg-rc-red text-white rounded-full py-3 font-bold md:col-span-2 disabled:opacity-60">
            {submitting ? "Submitting…" : order ? "Submit Asphalt Order Request" : "Submit Bid Request to Estimating"}
          </button>
          {status && <p className="md:col-span-2 text-sm" role="status" aria-live="polite">{status}</p>}
        </form>
      </section>
    </div>
  );
}
